import { BODY_FONT_PT, indentPaddingStyle, ORDERED_LIST_ATTRS, TAB_AS_NBSP } from '@root/helpers/editor';
import type { JSONContent } from '@tiptap/core';

import type {
  ClauseDraft,
  DraftBlock,
  DraftList,
  DraftParagraph,
  DraftRun,
  DraftTableCell,
  OrderedFormat,
  RunFormat,
} from '../draft/types';

const marksOf = (
  { bold, italic, underline, strike, editorSizePt }: RunFormat,
  inHeading: boolean,
  inLabelBox = false,
): JSONContent['marks'] => [
  ...(bold ? [{ type: 'bold' }] : []),
  ...(italic ? [{ type: 'italic' }] : []),
  ...(underline ? [{ type: 'underline' }] : []),
  ...(strike ? [{ type: 'strike' }] : []),
  ...(editorSizePt !== undefined && (editorSizePt !== BODY_FONT_PT || inHeading)
    ? [{ type: 'textStyle', attrs: { fontSize: `${editorSizePt}pt` } }]
    : []),
  ...(inLabelBox ? [{ type: 'labelBox' }] : []),
];

const inlineOf = (run: DraftRun, inHeading: boolean, inLabelBox = false): JSONContent =>
  run.kind === 'break'
    ? { type: 'hardBreak' }
    : {
        type: 'text',
        text: run.kind === 'tab' ? TAB_AS_NBSP : run.text,
        marks: marksOf(run.format, inHeading, inLabelBox),
      };

const inlinesOf = ({ runs, indentLevels }: DraftParagraph, inHeading: boolean): JSONContent[] => {
  const tab = runs.findIndex((run) => run.kind === 'tab');
  if (!indentLevels?.hanging || tab < 1) return runs.map((run) => inlineOf(run, inHeading));
  const label = runs.slice(0, tab);
  return [
    ...label.map((run) => inlineOf(run, inHeading, true)),
    inlineOf({ kind: 'text', text: '\u00a0', format: label[label.length - 1].format }, inHeading, true),
    ...runs.slice(tab + 1).map((run) => inlineOf(run, inHeading)),
  ];
};

const textblockAttrsOf = ({ indentLevels, align, lineSlot }: DraftParagraph) => ({
  ...indentLevels,
  textAlign: align,
  lineSlot,
});

const sizeAttrsOf = ({ runs }: DraftParagraph) => {
  const sizes = new Set(
    runs.flatMap((run) => (run.kind === 'text' && run.text.trim() ? [run.format.editorSizePt] : [])),
  );
  const [size] = sizes;
  return sizes.size === 1 && size !== undefined && size !== BODY_FONT_PT ? { fontSize: `${size}pt` } : {};
};

const paragraphOf = (paragraph: DraftParagraph): JSONContent =>
  paragraph.role.kind === 'heading'
    ? {
        type: 'heading',
        attrs: { level: paragraph.role.level, ...textblockAttrsOf(paragraph) },
        content: inlinesOf(paragraph, true),
      }
    : {
        type: 'paragraph',
        attrs: { ...textblockAttrsOf(paragraph), ...sizeAttrsOf(paragraph) },
        content: inlinesOf(paragraph, false),
      };

const cellOf = ({ blocks, colSpan, rowSpan }: DraftTableCell): JSONContent => ({
  type: 'tableCell',
  attrs: { colspan: colSpan, rowspan: rowSpan },
  content: blocks.length ? blocks.map(blockOf) : [{ type: 'paragraph' }],
});

const EDITOR_NUMBERING: Record<OrderedFormat, string> = {
  decimal: '1',
  'lower-roman': 'i',
  'upper-roman': 'I',
  'lower-alpha': 'a',
  'upper-alpha': 'A',
};

const LEGAL_NUMBERING = '1.1';

const listAttrsOf = ({ marker, textOffset, indentLevel }: DraftList) => {
  if (marker.format === 'bullet') return {};
  const type = marker.punctuation === 'legal' ? LEGAL_NUMBERING : EDITOR_NUMBERING[marker.format];
  return {
    textOffset,
    ...(indentLevel && { style: indentPaddingStyle(indentLevel) }),
    [ORDERED_LIST_ATTRS.start]: marker.start,
    [ORDERED_LIST_ATTRS.type]: type,
    [ORDERED_LIST_ATTRS.presetType]: type,
    ...(marker.punctuation !== 'legal' && { markerFormat: `${marker.punctuation}-${type}` }),
  };
};

const listOf = (list: DraftList): JSONContent => ({
  type: list.marker.format === 'bullet' ? 'bulletList' : 'orderedList',
  attrs: listAttrsOf(list),
  content: list.items.map(({ markerBold, blocks }) => ({
    type: 'listItem',
    attrs: { markerBold },
    content: blocks.map(blockOf),
  })),
});

const blockOf = (block: DraftBlock): JSONContent => {
  switch (block.kind) {
    case 'table':
      return { type: 'table', content: block.rows.map((cells) => ({ type: 'tableRow', content: cells.map(cellOf) })) };
    case 'list':
      return listOf(block);
    case 'paragraph':
      return paragraphOf(block);
  }
};

export const toClauseDoc = (draft: ClauseDraft): JSONContent => ({
  type: 'doc',
  content: draft.blocks.map(blockOf),
});
